/**
 * AuthProvider wraps the entire authenticated app and exposes the current
 * Supabase Auth user plus their app-specific profile (which carries the
 * role that drives every permission check). Mounted once, near the root.
 *
 * Deliberately minimal: no password reset UI beyond what Supabase Auth
 * gives for free, no account self-registration flow (accounts are
 * created directly in the Supabase dashboard or via the Admin Users page
 * — see PRD's locked scope: no email systems, no complex account mgmt).
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient';
import type { Profile, UserRole } from '../domain/types';

interface AuthContextValue {
  session: Session | null;
  profile: Profile | null;
  role: UserRole | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

async function fetchProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, full_name, role, created_at')
    .eq('id', userId)
    .single();

  if (error || !data) {
    // Every auth.users row gets a matching profiles row automatically,
    // created by the handle_new_auth_user trigger (see migration 0008)
    // the moment the account is created in Supabase Auth — there is no
    // longer a manual step required. A missing profile at this point is
    // therefore a genuine anomaly (the trigger was somehow removed, or
    // the row was deleted independently of the user), not an expected
    // setup gap. Still handled gracefully here rather than crashing, so
    // the UI can show a clear message instead of a blank screen.
    return null;
  }

  return {
    id: data.id,
    fullName: data.full_name,
    role: data.role,
    createdAt: data.created_at,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    supabase.auth.getSession().then(async ({ data }) => {
      if (!isMounted) return;
      setSession(data.session);
      if (data.session) {
        const p = await fetchProfile(data.session.user.id);
        if (isMounted) setProfile(p);
      }
      if (isMounted) setLoading(false);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange(async (_event, newSession) => {
      if (!isMounted) return;
      setSession(newSession);
      if (newSession) {
        const p = await fetchProfile(newSession.user.id);
        if (isMounted) setProfile(p);
      } else {
        setProfile(null);
      }
    });

    return () => {
      isMounted = false;
      subscription.subscription.unsubscribe();
    };
  }, []);

  async function signOut() {
    await supabase.auth.signOut();
  }

  const value: AuthContextValue = {
    session,
    profile,
    role: profile?.role ?? null,
    loading,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
